import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import ThemeSelector from '../../components/ThemeSelector';
import ThemeLivePreview from '../../components/ThemeLivePreview';
import StationeryGenerator from '../../components/StationeryGenerator';
import InviteStudio from '../../components/InviteStudio';
import { Eye, Palette } from '@phosphor-icons/react';
import { Tabs, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
} from '../../components/ui/dialog';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';
import InfoTip from '../../components/InfoTip';

const getThemeSnapshot = (event) => ({
  ...(event?.theme || {}),
  couple_logo_url: event?.branding?.couple_logo_url,
});

const mergeThemeUpdate = (currentTheme, nextTheme) => {
  if (!nextTheme) {
    return { couple_logo_url: currentTheme?.couple_logo_url };
  }

  if (nextTheme.colors && nextTheme.fonts) {
    return {
      ...currentTheme,
      selected_theme_id: nextTheme.id,
      selected_theme: nextTheme,
    };
  }

  return {
    ...currentTheme,
    ...nextTheme,
    selected_theme: nextTheme.selected_theme === undefined
      ? currentTheme?.selected_theme
      : nextTheme.selected_theme,
  };
};

function ThemePreviewPanel({ currentEvent, previewTheme, description, className = '' }) {
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const selectedTheme = previewTheme?.selected_theme;
  const backgroundImage = previewTheme?.selected_background_url
    || previewTheme?.custom_image_url
    || previewTheme?.generated_image_url;

  return (
    <div
      className={`overflow-hidden rounded-xl border-2 border-primary/35 bg-background shadow-lg ${className}`}
      data-testid="theme-preview-panel"
    >
      <div className="flex items-center justify-between gap-3 border-b bg-primary/10 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground">
            <Eye className="h-4 w-4" weight="bold" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">Guest view preview</p>
            <p className="text-[0.625rem] uppercase tracking-[0.2em] text-muted-foreground">Preview only</p>
          </div>
        </div>
        <span className="rounded-full border border-primary/25 bg-background px-2.5 py-1 text-[0.5625rem] font-bold uppercase tracking-[0.18em] text-primary">
          Live preview
        </span>
      </div>

      <div className="p-3 sm:p-4">
        {selectedTheme ? (
          <ThemeLivePreview
            event={currentEvent}
            theme={selectedTheme}
            backgroundImage={backgroundImage}
            description={description}
          />
        ) : (
          <div className="grid min-h-72 place-items-center rounded-lg border border-dashed bg-muted/35 px-6 text-center">
            <div>
              <Palette className="mx-auto h-9 w-9 text-muted-foreground" />
              <p className="mt-3 font-medium">Select a {eventTypeConfig.label} Theme</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Your guest-view preview will appear here immediately.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EventThemeSettings() {
  const { currentEvent, loadUser } = useAuth();
  const [searchParams] = useSearchParams();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const [previewTheme, setPreviewTheme] = useState(() => getThemeSnapshot(currentEvent));
  const [previewOpen, setPreviewOpen] = useState(false);
  const requestedTab = ['theme', 'stationery', 'invites'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'invites';

  useEffect(() => {
    setPreviewTheme(getThemeSnapshot(currentEvent));
  }, [currentEvent]);

  const handleThemeUpdate = async (newTheme) => {
    setPreviewTheme((existingTheme) => mergeThemeUpdate(existingTheme, newTheme));
    await loadUser();
  };

  if (!currentEvent) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Palette className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Please select an event first to customize its theme.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[90rem] space-y-6">
        {/* Theme and stationery remain available through existing direct links. */}
        <Tabs value={requestedTab} className="w-full">
          <TabsContent value="theme" className="mt-6">
            <div className="mb-4 flex justify-end xl:hidden">
              <Button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="gap-2"
                data-testid="open-theme-preview-btn"
                aria-label="Open guest view preview"
                title="Open guest view preview"
              >
                <Eye className="h-6 w-6" weight="bold" />
                Preview guest view
              </Button>
            </div>

            <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(25rem,0.82fr)]">
              <ThemeSelector
                eventId={currentEvent.id}
                eventData={currentEvent}
                currentTheme={previewTheme}
                onThemeUpdate={handleThemeUpdate}
              />

              <aside className="sticky top-6 hidden max-h-[calc(100vh-3rem)] overflow-y-auto pr-1 xl:block">
                <ThemePreviewPanel
                  currentEvent={currentEvent}
                  previewTheme={previewTheme}
                  description={eventTypeConfig.labels.settingsThemePreview}
                />
              </aside>
            </div>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
              <DialogContent className="w-[calc(100%_-_1rem)] max-w-3xl gap-0 overflow-hidden p-0 xl:hidden">
                <div className="overflow-y-auto p-3 pr-10 sm:p-5 sm:pr-12">
                  <ThemePreviewPanel
                    currentEvent={currentEvent}
                    previewTheme={previewTheme}
                    description={eventTypeConfig.labels.settingsThemePreview}
                    className="shadow-none"
                  />
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="stationery" className="mt-6">
            <StationeryGenerator
              eventId={currentEvent.id}
              currentTheme={currentEvent.theme}
              eventData={currentEvent}
            />
          </TabsContent>

          <TabsContent value="invites" className="mt-6">
            <InviteStudio
              eventId={currentEvent.id}
              eventData={currentEvent}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
